import { useEffect, useRef, useState } from "react";
import MerchPreviewRulers from "./MerchPreviewRulers";

function MerchGarmentMockup({
  garmentConfig,
  garmentImage,
  imageAlt,
  color,
  selectedColor = color.id,
  view,
  artworkUrl,
  placement,
  placementLimits,
  viewGeometry,
  previewSizeMode = "visual",
  printWidthInches,
  printWidthRatio,
  defaultPlacement,
  showRulers = false,
  showMeasurements = false,
  onPlacementChange,
  showPrintBoundary = false,
  compact = false,
  movable = false
}) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const isFront = view === "front";
  const usableGarmentImage = garmentImage && !imageFailed;
  const printArea = viewGeometry.printArea;
  const calibration = viewGeometry.calibration;
  const trueSize = calculateTrueSizeLayerWidth(garmentConfig, view, printArea, printWidthRatio);
  const effectivePreviewMode = previewSizeMode === "trueSize" && trueSize.valid ? "trueSize" : "visual";
  const artworkStyle = {
    left: `${placement.x + calibration.xOffset}%`,
    top: `${placement.y + calibration.yOffset}%`,
    width: `${(effectivePreviewMode === "trueSize" ? trueSize.layerWidthPercent : garmentConfig.artworkLayerWidths[view] * placement.scale) * calibration.scaleMultiplier}%`
  };
  const nearBoundary = movable && placementLimits && (
    placement.x - placementLimits.x.min < 1.5 || placementLimits.x.max - placement.x < 1.5 ||
    placement.y - placementLimits.y.min < 1.5 || placementLimits.y.max - placement.y < 1.5
  );

  useEffect(() => setImageFailed(false), [garmentImage]);

  const handleGarmentImageError = () => {
    console.warn(`[Merch Engine] Could not load ${garmentConfig.id} ${selectedColor} ${view} mockup; using rendered fallback.`);
    setImageFailed(true);
  };

  const handlePointerDown = (event) => {
    if (!movable || !onPlacementChange || !placementLimits) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY, startX: placement.x, startY: placement.y };
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !containerRef.current) return;
    event.preventDefault();
    const bounds = containerRef.current.getBoundingClientRect();
    onPlacementChange({
      ...placement,
      x: clamp(drag.startX + ((event.clientX - drag.startClientX) / bounds.width) * 100, placementLimits.x.min, placementLimits.x.max),
      y: clamp(drag.startY + ((event.clientY - drag.startClientY) / bounds.height) * 100, placementLimits.y.min, placementLimits.y.max)
    });
  };

  const endDrag = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (event) => {
    if (!movable || !onPlacementChange || !placementLimits) return;
    const distance = event.shiftKey ? 5 : 1;
    const delta = { ArrowLeft: [-distance, 0], ArrowRight: [distance, 0], ArrowUp: [0, -distance], ArrowDown: [0, distance] }[event.key];
    if (!delta) return;
    event.preventDefault();
    onPlacementChange({
      ...placement,
      x: clamp(placement.x + delta[0], placementLimits.x.min, placementLimits.x.max),
      y: clamp(placement.y + delta[1], placementLimits.y.min, placementLimits.y.max)
    });
  };

  return (
    <div ref={containerRef} className={compact ? "merch-garment-mockup compact" : "merch-garment-mockup"} role="img" aria-label={`${selectedColor} ${garmentConfig.name} ${view} mockup`} style={{ ...containerStyle, aspectRatio: garmentConfig.aspectRatio }}>
      <div data-mockup-layer="garment-reference" style={fixedReferenceLayerStyle}>
        {usableGarmentImage ? (
          <img src={garmentImage} alt={imageAlt} onError={handleGarmentImageError} draggable={false} style={realGarmentImageStyle} />
        ) : (
          <FallbackGarment garmentConfig={garmentConfig} color={color} view={view} compact={compact} />
        )}
      </div>

      <span style={assetStatusStyle}>{usableGarmentImage ? "Real garment mockup" : "Rendered garment fallback"}</span>
      {previewSizeMode === "trueSize" && !trueSize.valid && <span role="alert" style={trueSizeErrorStyle}>Unable to calculate True Size.</span>}

      {artworkUrl && (
        <div
          data-mockup-layer="artwork"
          tabIndex={movable ? 0 : undefined}
          role={movable ? "button" : undefined}
          aria-label={movable ? `Identity Tee ${view} artwork. Drag or use arrow keys to adjust placement.` : undefined}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{ ...artworkLayerStyle, ...artworkStyle, cursor: movable ? (isDragging ? "grabbing" : "grab") : "default", outline: movable && (isFocused || isDragging) ? `2px solid ${nearBoundary ? "#ff8b87" : "rgba(225,6,0,.7)"}` : "2px solid transparent", outlineOffset: "3px", touchAction: movable ? "none" : "auto", userSelect: "none", transition: isDragging ? "none" : artworkLayerStyle.transition }}
        >
          <img src={artworkUrl} alt="" aria-hidden="true" draggable={false} style={artworkImageStyle} />
          {movable && <span aria-hidden="true" style={dragHintStyle}>{nearBoundary ? "Placement limit" : "Drag to position"}</span>}
        </div>
      )}

      {showPrintBoundary && (
        <div data-mockup-layer="print-boundary-reference" style={fixedBoundaryLayerStyle}>
          <PrintBoundary printArea={printArea} isFront={isFront} />
        </div>
      )}
      <MerchPreviewRulers printArea={printArea} printWidthInches={printWidthInches} showRulers={showRulers} showMeasurements={showMeasurements} view={view} horizontalOffset={placement.x - defaultPlacement.x} verticalOffset={placement.y - defaultPlacement.y} />
    </div>
  );
}

function FallbackGarment({ garmentConfig, color, view, compact }) {
  const gradientId = `fabric-${color.id.replace(/\s/g, "-")}-${view}-${compact ? "small" : "large"}`;
  return (
    <svg aria-hidden="true" viewBox={garmentConfig.fallbackSilhouette.viewBox} style={garmentSvgStyle}>
      <defs><linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" stopOpacity="0.08" /><stop offset="0.5" stopColor="#fff" stopOpacity="0" /><stop offset="1" stopColor="#000" stopOpacity="0.16" /></linearGradient></defs>
      <path d={garmentConfig.fallbackSilhouette.bodyPath} fill={color.hex} stroke="rgba(255,255,255,.12)" strokeWidth="3" />
      <path d={garmentConfig.fallbackSilhouette.bodyPath} fill={`url(#${gradientId})`} />
      <path d={garmentConfig.fallbackSilhouette.collarPath} fill="#090909" fillOpacity={color.artworkTone === "light" ? ".18" : ".72"} />
      <path d="M342 54 Q400 104 458 54" fill="none" stroke={color.artworkTone === "light" ? "#aaa294" : "#494949"} strokeWidth="12" strokeLinecap="round" />
    </svg>
  );
}

function PrintBoundary({ printArea, isFront }) {
  return (
    <div aria-hidden="true" style={{ ...boundaryStyle, left: `${printArea.left}%`, top: `${printArea.top}%`, width: `${printArea.width}%`, height: `${printArea.height}%` }}>
      <span style={guideLabelStyle}>{isFront ? "Centered chest guide" : "Print area"}</span>
      {!isFront && <span style={centerGuideStyle} />}
    </div>
  );
}

function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }

function calculateTrueSizeLayerWidth(garmentConfig, view, printArea, printWidthRatio) {
  const artworkContentRatio = garmentConfig.artworkContentWidthRatios?.[view];
  const layerWidthPercent = (printArea?.width * printWidthRatio) / artworkContentRatio;
  const valid = [printArea?.width, printWidthRatio, artworkContentRatio, layerWidthPercent]
    .every((value) => Number.isFinite(value) && value > 0);
  return { valid, layerWidthPercent: valid ? layerWidthPercent : null };
}

const containerStyle = { position: "relative", width: "100%", overflow: "hidden", borderRadius: "16px", background: "#101010" };
// Garment and boundary geometry are fixed reference layers. Preview modes may only alter artworkLayerStyle.
const fixedReferenceLayerStyle = { position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" };
const fixedBoundaryLayerStyle = { position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" };
const garmentSvgStyle = { position: "absolute", inset: 0, display: "block", width: "100%", height: "100%" };
const realGarmentImageStyle = { position: "absolute", inset: 0, display: "block", width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", pointerEvents: "none" };
const artworkLayerStyle = { position: "absolute", display: "block", height: "auto", transform: "translate(-50%, -50%)", transition: "left 80ms ease, top 80ms ease, width 120ms ease", zIndex: 2 };
const artworkImageStyle = { display: "block", width: "100%", height: "auto", pointerEvents: "none" };
const assetStatusStyle = { position: "absolute", top: "12px", right: "12px", zIndex: 4, padding: "6px 9px", border: "1px solid rgba(255,255,255,.14)", borderRadius: "999px", background: "rgba(10,10,10,.78)", color: "#bbb", fontSize: "9px", fontWeight: 900, letterSpacing: ".6px", textTransform: "uppercase" };
const trueSizeErrorStyle = { position: "absolute", left: "12px", top: "12px", zIndex: 6, padding: "7px 10px", border: "1px solid #e10600", borderRadius: "8px", background: "rgba(10,10,10,.9)", color: "#ffaaa7", fontSize: "11px" };
const dragHintStyle = { position: "absolute", left: "50%", bottom: "4%", transform: "translateX(-50%)", padding: "5px 8px", borderRadius: "999px", background: "rgba(10,10,10,.82)", color: "#ddd", fontSize: "9px", fontWeight: 900, letterSpacing: ".7px", textTransform: "uppercase", whiteSpace: "nowrap", pointerEvents: "none" };
const boundaryStyle = { position: "absolute", border: "1px dashed rgba(225,6,0,.72)", borderRadius: "6px", pointerEvents: "none" };
const guideLabelStyle = { position: "absolute", top: "7px", left: "9px", color: "#e10600", fontSize: "9px", fontWeight: 900, letterSpacing: ".7px", textTransform: "uppercase" };
const centerGuideStyle = { position: "absolute", top: 0, bottom: 0, left: "50%", borderLeft: "1px dashed rgba(255,255,255,.24)" };

export default MerchGarmentMockup;
